/* eslint-disable max-lines-per-function */
import React, { useState } from "react";

import { Form, Formik, useField } from "formik";
import { CircularProgress, Grid } from "@mui/material";
import { useMutation } from "@apollo/client";
import * as yup from "yup";
import { SelectAutocomplete } from "components/haven-design/form/select";

import { useSnackbar } from "components/haven-design/snackbar";

import { AdminListUsersQuery_adminApplicants as AdminApplicant, AdminMergeAccountsMutation, AdminMergeAccountsMutationVariables } from "types/gql-types";
import { DialogActions, DialogContent } from "components/haven-design/modal/dialog";
import { Modal, Button } from "components/haven-design";
import { MergeAccountsMutation } from "./queries";


const mergeAccountSchema = yup.object({
  firstName: yup.string(),
  lastName: yup.string(),
  loginEmail: yup.string().nullable(),
  loginPhone: yup.string().nullable(),
});

type Values = yup.InferType<typeof mergeAccountSchema>
const initialValues: Values = {
  firstName: "",
  lastName: "",
  loginEmail: "",
  loginPhone: "",
};

type TypeaheadTextInputProps = { name: string, label: string, options: string[] }
const TypeaheadTextInput = ({ name, label, options }: TypeaheadTextInputProps) => {
  const [{ value },, { setValue }] = useField<string>(name);

  return (
    <SelectAutocomplete
      freeSolo
      label={label}
      options={options}
      value={value}
      onChange={(_, newValue) => newValue && setValue(newValue)}
      onInputChange={(_, newValue) => setValue(newValue)} />
  );
};

type Props = {
  users: AdminApplicant[]
  close: () => void
}

const MergeAccounts: React.FC<Props> = ({ users, close }) => {
  const { openSnackbar } = useSnackbar();
  const [mergeAccounts, { loading }] = useMutation<
  AdminMergeAccountsMutation,
  AdminMergeAccountsMutationVariables
  >(MergeAccountsMutation);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [pendingValues, setPendingValues] = useState<Values | null>(null);

  const handleMergeAccounts = (variables: Values) => {
    setPendingValues(variables);
    setShowConfirmation(true);
  };

  const confirmMerge = async () => {
    if (!pendingValues) return;
    setShowConfirmation(false);

    try {
      const ids = users.map(({ id }) => id);
      await mergeAccounts({ variables: { ids, ...pendingValues } });

      openSnackbar({ message: "Merged Accounts Successfully", status: "success" });
      close();
    } catch {
      openSnackbar({ message: "Merging Accounts Failed", status: "error" });
    } finally {
      setPendingValues(null);
    }
  };

  const cancelMerge = () => {
    setShowConfirmation(false);
    setPendingValues(null);
  };

  const firstNames = users.map(({ profile }) => (profile?.firstName)).filter(Boolean) as string[];
  const lastNames = users.map(({ profile }) => (profile?.lastName)).filter(Boolean) as string[];
  const loginEmails = users.map(({ loginEmail }) => (loginEmail)).filter(Boolean) as string[];
  const loginPhones = users.map(({ loginPhone }) => (loginPhone)).filter(Boolean) as string[];

  return (
    <>
      <Formik
        initialValues={initialValues}
        validationSchema={mergeAccountSchema}
        onSubmit={handleMergeAccounts}>
        <>
          <DialogContent>
            <Form id="merge-accounts">
              <Grid container>
                <Grid size={{ xs: 12 }}>
                  <p>
                Merging {users.length} user accounts into one.
                Please pick the best name and phone for the final account.
                When merged, all interests will be merged into a single account.
                  </p>
                </Grid>

                {loading && (
                  <Grid container justifyContent="center">
                    <CircularProgress />
                    <p>Merging Accounts ...</p>
                  </Grid>
                )}

                {!loading && (
                  <>
                    <Grid size={{ xs: 12 }}>
                      <TypeaheadTextInput name="firstName" label="First Name" options={firstNames} />
                    </Grid>

                    <Grid size={{ xs: 12 }}>
                      <TypeaheadTextInput name="lastName" label="Last Name" options={lastNames} />
                    </Grid>

                    <Grid size={{ xs: 12 }}>
                      <TypeaheadTextInput name="loginEmail" label="Login Email" options={loginEmails} />
                    </Grid>

                    <Grid size={{ xs: 12 }}>
                      <TypeaheadTextInput name="loginPhone" label="Login Phone" options={loginPhones} />
                    </Grid>

                  </>
                )}
              </Grid>
            </Form>
          </DialogContent>
          <DialogActions>
            <Button color="secondary" type="button" onClick={close}>Cancel</Button>
            <Button color="primary" type="submit" form="merge-accounts">Merge</Button>
          </DialogActions>
        </>
      </Formik>

      <Modal
        isOpen={showConfirmation}
        onRequestClose={cancelMerge}
        title="Confirm Account Merge"
      >
        <DialogContent>
          <p>Are you sure you want to merge these accounts?</p>
          <p>This action cannot be undone.</p>
        </DialogContent>
        <DialogActions>
          <Button
            size="L"
            color="secondary"
            onClick={cancelMerge}
          >
            Cancel
          </Button>
          <Button
            size="L"
            color="primary"
            onClick={confirmMerge}
          >
            Merge Accounts
          </Button>
        </DialogActions>
      </Modal>
    </>
  );
};

export default MergeAccounts;
